import React, { useEffect, useMemo, useState } from "react";
import "../DashBoard/DashBoard.css";
import ActiveBookLottery from "./ActiveBookLottery";
import TopSellingGame from "./TopSellingGame";
import TicketsSoldChart from "./TicketsSoldChart";
import useApi from "../../../../utils/api-manager/Helper/useApi";
import {
  Trophy,
  Ticket,
  BookOpen,
  TrendingUp,
  AlertCircle,
  CalendarDays,
  BarChart3,
  Activity,
  Gamepad2,
  RefreshCw,
} from "lucide-react";

const safeArray = (value) => (Array.isArray(value) ? value : []);

const safeNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const formatCount = (value) => safeNumber(value).toLocaleString("en-IN");

const pickNumber = (item, keys = []) => {
  if (!item || typeof item !== "object") return 0;

  for (const key of keys) {
    if (item[key] !== undefined && item[key] !== null && item[key] !== "") {
      return safeNumber(item[key]);
    }
  }

  return 0;
};

const sumByPossibleKeys = (items, keys) =>
  safeArray(items).reduce((sum, item) => sum + pickNumber(item, keys), 0);

const getFirstAvailableValue = (item, keys = []) => {
  if (!item || typeof item !== "object") return "";

  for (const key of keys) {
    if (item[key] !== undefined && item[key] !== null && item[key] !== "") {
      return item[key];
    }
  }

  return "";
};

const KpiCard = ({ icon: Icon, label, value, helper, tone = "blue" }) => (
  <div className={`lotdb-kpi-card lotdb-tone-${tone}`}>
    <div className="lotdb-kpi-icon">
      <Icon size={21} />
    </div>
    <div className="lotdb-kpi-content">
      <div className="lotdb-kpi-label">{label}</div>
      <div className="lotdb-kpi-value">{value}</div>
      {helper && <div className="lotdb-kpi-helper">{helper}</div>}
    </div>
  </div>
);

const InsightCard = ({ icon: Icon, title, value, note, tone = "blue" }) => (
  <div className={`lotdb-insight-card lotdb-tone-${tone}`}>
    <div className="lotdb-insight-top">
      <div className="lotdb-insight-icon">
        <Icon size={19} />
      </div>
      <div>
        <h3>{title}</h3>
        <p>{note}</p>
      </div>
    </div>
    <div className="lotdb-insight-value">{value}</div>
  </div>
);

const SectionHeader = ({ icon: Icon, title, subtitle }) => (
  <div className="lotdb-section-header">
    <div className="lotdb-section-icon">
      <Icon size={18} />
    </div>
    <div>
      <h2>{title}</h2>
      {subtitle && <p>{subtitle}</p>}
    </div>
  </div>
);

const EmptyState = ({ message }) => (
  <div className="lotdb-empty-state">
    <Ticket size={30} />
    <h2>No Lottery Dashboard Data</h2>
    <p>{message}</p>
  </div>
);

export default function Dashboard() {
  const { Post } = useApi();

  const [loading, setLoading] = useState(true);
  const [activeGame, setActiveGame] = useState([]);
  const [activeBookSummary, setActiveBookSummary] = useState([]);
  const [activeThisMonth, setActiveThisMonth] = useState([]);
  const [error, setError] = useState("");

  useEffect(() => {
    fetchActiveLottery();
  }, []);

  const fetchActiveLottery = async () => {
    try {
      setLoading(true);
      setError("");

      const currentYear = new Date().getFullYear();
      const startDate = `${currentYear}-01-01`;
      const endDate = `${currentYear}-12-31`;

      const response = await Post("lotteryDashboardReport", {
        start_date: startDate,
        end_date: endDate,
      });

      const {
        active_lottery_games = [],
        activated_book_ticket_summary = [],
        top_10_selling_games_activated_this_month = [],
      } = response?.data || {};

      setActiveGame(safeArray(active_lottery_games));
      setActiveBookSummary(safeArray(activated_book_ticket_summary));
      setActiveThisMonth(safeArray(top_10_selling_games_activated_this_month));
    } catch (fetchError) {
      console.error("Error fetching lottery dashboard data:", fetchError);
      setError(fetchError?.message || "Failed to fetch lottery dashboard data.");
    } finally {
      setLoading(false);
    }
  };

  const activeGameList = safeArray(activeGame);
  const activeBookSummaryList = safeArray(activeBookSummary);
  const topSellingList = safeArray(activeThisMonth);

  const hasData =
    activeGameList.length > 0 ||
    activeBookSummaryList.length > 0 ||
    topSellingList.length > 0;

  const totalTicketsSold = useMemo(
    () =>
      sumByPossibleKeys(activeBookSummaryList, [
        "tickets_sold",
        "sold_tickets",
        "total_sold",
        "sold",
        "sold_count",
        "total_sold_tickets",
      ]),
    [activeBookSummaryList]
  );

  const totalTicketsAvailable = useMemo(
    () =>
      sumByPossibleKeys(activeBookSummaryList, [
        "available_tickets",
        "remaining_tickets",
        "remaining",
        "available",
        "unsold_tickets",
      ]),
    [activeBookSummaryList]
  );

  const topGameName = useMemo(() => {
    const firstGame = topSellingList[0];

    return (
      getFirstAvailableValue(firstGame, [
        "game_name",
        "lottery_name",
        "name",
        "title",
        "game",
      ]) || "No top game yet"
    );
  }, [topSellingList]);

  const currentDateLabel = new Date().toLocaleDateString("en-IN", {
    weekday: "long",
    day: "2-digit",
    month: "short",
    year: "numeric",
  });

  if (loading) {
    return (
      <div className="responsive-box lotdb-page">
        <style>{dashboardStyles}</style>
        <div className="lotdb-loading">
          <div className="lotdb-spinner" />
          <div>
            <h3>Loading Lottery Dashboard</h3>
            <p>Preparing game, ticket and book insights.</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="responsive-box lotdb-page">
        <style>{dashboardStyles}</style>
        <div className="lotdb-empty-page lotdb-error-page">
          <AlertCircle size={38} />
          <h2>Unable to Load Dashboard</h2>
          <p>{error}</p>
          <button type="button" onClick={fetchActiveLottery} className="lotdb-retry-btn">
            <RefreshCw size={16} />
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (!hasData) {
    return (
      <div className="responsive-box lotdb-page">
        <style>{dashboardStyles}</style>
        <div className="lotdb-empty-page">
          <Ticket size={38} />
          <h2>No Lottery Data Found</h2>
          <p>Activate lottery games and books to start viewing dashboard insights.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="responsive-box lotdb-page">
      <style>{dashboardStyles}</style>

      <div className="lotdb-shell">
        <section className="lotdb-hero">
          <div>
            <div className="lotdb-kicker">
              <span className="lotdb-kicker-dot" />
              Lottery Business View
            </div>
            <h1>Lottery Dashboard</h1>
            <p>
              Simple overview for active games, activated books, ticket movement and top selling lottery games.
            </p>
            <div className="lotdb-date-pill">
              <CalendarDays size={14} />
              {currentDateLabel}
            </div>
          </div>

          <div className="lotdb-hero-kpis">
            <KpiCard
              icon={Gamepad2}
              label="Active Games"
              value={formatCount(activeGameList.length)}
              helper="Currently active lottery games"
              tone="blue"
            />
            <KpiCard
              icon={BookOpen}
              label="Activated Books"
              value={formatCount(activeBookSummaryList.length)}
              helper="Book records in dashboard"
              tone="purple"
            />
            <KpiCard
              icon={Trophy}
              label="Top Game"
              value={topGameName}
              helper="Best selling game this month"
              tone="amber"
            />
          </div>
        </section>

        <section className="lotdb-insight-grid">
          <InsightCard
            icon={Ticket}
            title="Tickets Sold"
            value={formatCount(totalTicketsSold)}
            note="Calculated from activated book summary"
            tone="green"
          />
          <InsightCard
            icon={Activity}
            title="Tickets Available"
            value={formatCount(totalTicketsAvailable)}
            note="Remaining or available ticket count"
            tone="blue"
          />
          <InsightCard
            icon={TrendingUp}
            title="Top Selling Games"
            value={formatCount(topSellingList.length)}
            note="Active this month"
            tone="amber"
          />
        </section>

        <section className="lotdb-main-grid">
          <div className="lotdb-panel">
            <SectionHeader
              icon={Trophy}
              title="Top Selling Games"
              subtitle="Best performing games activated this month"
            />
            <div className="lotdb-component-frame">
              <TopSellingGame activeThisMonth={topSellingList} />
            </div>
          </div>

          <div className="lotdb-panel">
            <SectionHeader
              icon={BarChart3}
              title="Tickets Sold Chart"
              subtitle="Book-wise ticket sales movement"
            />
            <div className="lotdb-component-frame">
              <TicketsSoldChart activeBookSummary={activeBookSummaryList} />
            </div>
          </div>
        </section>

        <section className="lotdb-panel">
          <SectionHeader
            icon={BookOpen}
            title="Active Book Lottery"
            subtitle="Active lottery games and book details"
          />
          <div className="lotdb-component-frame">
            <ActiveBookLottery activeGame={activeGameList} />
          </div>
        </section>
      </div>
    </div>
  );
}

const dashboardStyles = `
  .lotdb-page {
    min-height: 100vh;
    padding: 10px 12px 28px;
    background:
      radial-gradient(circle at top left, rgba(37, 99, 235, 0.08), transparent 28%),
      radial-gradient(circle at top right, rgba(245, 158, 11, 0.08), transparent 28%),
      linear-gradient(180deg, #f8fafc 0%, #ffffff 100%);
    color: #0f172a;
  }

  .lotdb-shell {
    max-width: 1450px;
    margin: 0 auto;
  }

  .lotdb-hero {
    position: relative;
    overflow: hidden;
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(440px, 0.85fr);
    gap: 18px;
    align-items: center;
    padding: 22px;
    margin-bottom: 14px;
    border-radius: 28px;
    color: #ffffff;
    background: linear-gradient(135deg, #020617 0%, #111827 56%, #92400e 100%);
    box-shadow: 0 24px 70px rgba(15, 23, 42, 0.22);
  }

  .lotdb-hero::after {
    content: "";
    position: absolute;
    width: 320px;
    height: 320px;
    right: -120px;
    top: -160px;
    border-radius: 999px;
    background: rgba(251, 191, 36, 0.16);
  }

  .lotdb-hero > * {
    position: relative;
    z-index: 1;
  }

  .lotdb-kicker {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 6px 11px;
    border-radius: 999px;
    margin-bottom: 10px;
    font-size: 11px;
    font-weight: 950;
    color: #fef3c7;
    background: rgba(255, 255, 255, 0.13);
    border: 1px solid rgba(255, 255, 255, 0.22);
  }

  .lotdb-kicker-dot {
    width: 7px;
    height: 7px;
    border-radius: 999px;
    background: #fbbf24;
    box-shadow: 0 0 0 4px rgba(251, 191, 36, 0.18);
  }

  .lotdb-hero h1 {
    margin: 0;
    font-size: 31px;
    line-height: 1.08;
    font-weight: 950;
    letter-spacing: -0.045em;
  }

  .lotdb-hero p {
    max-width: 700px;
    margin: 8px 0 0;
    color: rgba(255, 255, 255, 0.80);
    font-size: 13px;
    font-weight: 700;
    line-height: 1.6;
  }

  .lotdb-date-pill {
    display: inline-flex;
    align-items: center;
    gap: 7px;
    margin-top: 13px;
    padding: 7px 11px;
    border-radius: 999px;
    color: #fde68a;
    background: rgba(255, 255, 255, 0.10);
    border: 1px solid rgba(255, 255, 255, 0.18);
    font-size: 11px;
    font-weight: 900;
  }

  .lotdb-hero-kpis {
    display: grid;
    grid-template-columns: 1fr;
    gap: 10px;
  }

  .lotdb-kpi-card,
  .lotdb-insight-card,
  .lotdb-panel {
    background: rgba(255, 255, 255, 0.96);
    border: 1px solid #dbe3ec;
    box-shadow: 0 16px 42px rgba(15, 23, 42, 0.075);
  }

  .lotdb-kpi-card {
    display: flex;
    align-items: center;
    gap: 13px;
    min-height: 82px;
    padding: 13px;
    border-radius: 20px;
  }

  .lotdb-kpi-icon,
  .lotdb-insight-icon,
  .lotdb-section-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    color: var(--lotdb-accent);
    background: var(--lotdb-soft);
    border: 1px solid var(--lotdb-border);
    flex-shrink: 0;
  }

  .lotdb-kpi-icon {
    width: 44px;
    height: 44px;
    border-radius: 15px;
  }

  .lotdb-kpi-label {
    color: #475569;
    font-size: 11px;
    font-weight: 950;
    letter-spacing: 0.04em;
    text-transform: uppercase;
  }

  .lotdb-kpi-value {
    margin-top: 2px;
    color: #0f172a;
    font-size: 20px;
    font-weight: 950;
    letter-spacing: -0.035em;
    line-height: 1.15;
  }

  .lotdb-kpi-helper {
    color: #64748b;
    font-size: 11px;
    font-weight: 750;
  }

  .lotdb-insight-grid,
  .lotdb-main-grid {
    display: grid;
    gap: 14px;
    margin-bottom: 14px;
  }

  .lotdb-insight-grid {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }

  .lotdb-main-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .lotdb-insight-card {
    border-radius: 22px;
    padding: 15px;
  }

  .lotdb-insight-top {
    display: flex;
    gap: 11px;
    align-items: flex-start;
  }

  .lotdb-insight-icon {
    width: 42px;
    height: 42px;
    border-radius: 15px;
  }

  .lotdb-insight-card h3 {
    margin: 0;
    color: #0f172a;
    font-size: 14px;
    font-weight: 950;
  }

  .lotdb-insight-card p {
    margin: 4px 0 0;
    color: #64748b;
    font-size: 11px;
    font-weight: 750;
    line-height: 1.45;
  }

  .lotdb-insight-value {
    margin-top: 14px;
    color: #0f172a;
    font-size: 26px;
    font-weight: 950;
    letter-spacing: -0.04em;
  }

  .lotdb-panel {
    border-radius: 24px;
    padding: 16px;
    margin-bottom: 14px;
  }

  .lotdb-section-header {
    display: flex;
    align-items: center;
    gap: 11px;
    margin-bottom: 14px;
  }

  .lotdb-section-icon {
    width: 40px;
    height: 40px;
    border-radius: 15px;
    --lotdb-accent: #0f172a;
    --lotdb-soft: #f8fafc;
    --lotdb-border: #dbe3ec;
  }

  .lotdb-section-header h2 {
    margin: 0;
    color: #0f172a;
    font-size: 17px;
    font-weight: 950;
    letter-spacing: -0.025em;
  }

  .lotdb-section-header p {
    margin: 3px 0 0;
    color: #64748b;
    font-size: 12px;
    font-weight: 750;
  }

  .lotdb-component-frame {
    width: 100%;
    overflow-x: auto;
  }

  .lotdb-component-frame .component-card,
  .lotdb-component-frame > div {
    box-shadow: none !important;
  }

  .lotdb-tone-blue {
    --lotdb-accent: #2563eb;
    --lotdb-soft: #eff6ff;
    --lotdb-border: #bfdbfe;
  }

  .lotdb-tone-green {
    --lotdb-accent: #059669;
    --lotdb-soft: #ecfdf5;
    --lotdb-border: #a7f3d0;
  }

  .lotdb-tone-purple {
    --lotdb-accent: #7c3aed;
    --lotdb-soft: #f3e8ff;
    --lotdb-border: #ddd6fe;
  }

  .lotdb-tone-amber {
    --lotdb-accent: #d97706;
    --lotdb-soft: #fffbeb;
    --lotdb-border: #fde68a;
  }

  .lotdb-loading {
    min-height: 420px;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 18px;
    color: #0f172a;
  }

  .lotdb-loading h3 {
    margin: 0;
    font-size: 18px;
    font-weight: 950;
  }

  .lotdb-loading p {
    margin: 5px 0 0;
    color: #64748b;
    font-size: 13px;
    font-weight: 750;
  }

  .lotdb-spinner {
    width: 54px;
    height: 54px;
    border-radius: 999px;
    border: 5px solid #fef3c7;
    border-top-color: #d97706;
    animation: lotdb-spin 0.8s linear infinite;
  }

  @keyframes lotdb-spin {
    to {
      transform: rotate(360deg);
    }
  }

  .lotdb-empty-page,
  .lotdb-empty-state {
    min-height: 430px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-direction: column;
    text-align: center;
    color: #0f172a;
  }

  .lotdb-empty-page svg,
  .lotdb-empty-state svg {
    color: #d97706;
    margin-bottom: 14px;
  }

  .lotdb-error-page svg {
    color: #dc2626;
  }

  .lotdb-empty-page h2,
  .lotdb-empty-state h2 {
    margin: 0;
    font-size: 22px;
    font-weight: 950;
  }

  .lotdb-empty-page p,
  .lotdb-empty-state p {
    max-width: 480px;
    margin: 8px 0 0;
    color: #64748b;
    font-size: 13px;
    font-weight: 750;
  }

  .lotdb-retry-btn {
    margin-top: 14px;
    border: none;
    border-radius: 999px;
    height: 40px;
    padding: 0 16px;
    display: inline-flex;
    align-items: center;
    gap: 8px;
    background: #92400e;
    color: #ffffff;
    font-size: 13px;
    font-weight: 950;
    cursor: pointer;
    box-shadow: 0 10px 24px rgba(146, 64, 14, 0.20);
  }

  @media (max-width: 1200px) {
    .lotdb-hero {
      grid-template-columns: 1fr;
    }

    .lotdb-insight-grid {
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }

    .lotdb-main-grid {
      grid-template-columns: 1fr;
    }
  }

  @media (max-width: 768px) {
    .lotdb-page {
      padding: 8px 8px 22px;
    }

    .lotdb-hero {
      border-radius: 22px;
      padding: 18px;
    }

    .lotdb-hero h1 {
      font-size: 25px;
    }

    .lotdb-insight-grid {
      grid-template-columns: 1fr;
    }

    .lotdb-panel,
    .lotdb-insight-card {
      border-radius: 20px;
      padding: 14px;
    }
  }
`;
